import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { buildDownloadFilename } from '../repository/common/downloadFileName';
import useGroupRepo from '../repository/groupsRepo';
import {
  useErisInformationStore,
  ErisStatusSearchingParameters,
  Disaster,
  ErisStatusResultsModel,
} from '../store/ErisInformationStore';
import {
  prepareCSVStatusRequestBody,
  prepareCSVSummaryRequestBody,
} from './hepler/ErisCSVRequestBody';
import { partialErisStatusSearchingParameters } from '../repository/common/partialSearchingParameters';
import useWorkplacesRepo from '../repository/workplacesRepo';

type CsvDownloadOptions = {
  latest?: boolean;
  disaster?: Disaster | null;
};

function useGroupService() {
  const { t } = useTranslation();
  const { searchGroupsRequest } = useGroupRepo();
  const { getGroupStatus, getGroupStatusCSV, getGroupSummary, getGroupSummaryCSV } =
    useWorkplacesRepo();
  const {
    setAvailableGroups,
    setIsSearchingResults,
    setStatusResult,
    statusSearchingParameters,
    setStatusSummaryList,
    patchStatusPagination,
  } = useErisInformationStore();

  const executeSearch = useCallback(
    async <T>(searchFn: () => Promise<T>, setter: (data: T) => void) => {
      setIsSearchingResults(true);
      try {
        const result = await searchFn();
        setter(result);
      } finally {
        setIsSearchingResults(false);
      }
    },
    [setIsSearchingResults]
  );

  const searchGroups = useCallback(
    async (groupId?: string) => {
      const groups = await searchGroupsRequest(groupId);
      setAvailableGroups(groups ?? []);
    },
    [searchGroupsRequest, setAvailableGroups]
  );

  const searchGroupStatus = useCallback(
    (overridingParams?: ErisStatusSearchingParameters) => {
      const params = overridingParams ?? statusSearchingParameters;
      if (overridingParams) {
        patchStatusPagination({
          searchParams: overridingParams.searchParams,
          pageNumber: overridingParams.pageNumber,
          pageSize: overridingParams.pageSize,
        });
      }
      return executeSearch(
        () => getGroupStatus(params) as unknown as Promise<ErisStatusResultsModel>,
        setStatusResult
      );
    },
    [
      getGroupStatus,
      setStatusResult,
      statusSearchingParameters,
      executeSearch,
      patchStatusPagination,
    ]
  );

  const searchGroupSummary = useCallback(
    (overridingParams?: ErisStatusSearchingParameters) => {
      const params = overridingParams ?? statusSearchingParameters;
      if (overridingParams) {
        patchStatusPagination({ searchParams: overridingParams.searchParams });
      }
      return executeSearch(
        () => getGroupSummary(params),
        (data) => setStatusSummaryList(data ?? null)
      );
    },
    [
      getGroupSummary,
      setStatusSummaryList,
      statusSearchingParameters,
      executeSearch,
      patchStatusPagination,
    ]
  );

  const prepareGroupStatusCsvDownload = useCallback(
    async (options: CsvDownloadOptions = { latest: true }) => {
      const { latest = true, disaster = null } = options;
      const body = prepareCSVStatusRequestBody(
        t,
        partialErisStatusSearchingParameters(statusSearchingParameters, [
          'groupName',
          'userId',
          'buildingName',
          'operationStatus',
        ]),
        latest,
        disaster
      );
      const blob = await getGroupStatusCSV(body);
      return { blob, filename: buildDownloadFilename('L') };
    },
    [getGroupStatusCSV, statusSearchingParameters, t]
  );

  const prepareGroupSummaryCsvDownload = useCallback(
    async (options: CsvDownloadOptions = { latest: true }) => {
      const { latest = true, disaster = null } = options;

      const { groupName, userId } = statusSearchingParameters.searchParams;

      if (!userId) {
        throw new Error('User ID is missing from the store. Cannot prepare CSV download.');
      }
      const body = prepareCSVSummaryRequestBody(t, groupName ?? '', latest, userId, disaster);
      const blob = await getGroupSummaryCSV(body);
      return { blob, filename: buildDownloadFilename('S') };
    },
    [getGroupSummaryCSV, statusSearchingParameters, t]
  );

  return {
    searchGroups,
    searchGroupStatus,
    prepareGroupStatusCsvDownload,
    searchGroupSummary,
    prepareGroupSummaryCsvDownload,
  };
}

export default useGroupService;
